import cx from "classnames";
import { FC, SVGProps } from "react";

import { Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import RepeatCash from "atoms/icon/icons/RepeatCash.svg";
import { AnimatedBackground } from "components/AnimatedBackground/AnimatedBackground";
import { ExpandableItems } from "molecules/expandable-items/ExpandableItems";
import { TreatmentCard } from "molecules/treatment-card/TreatmentCard";
import { IfFlag, useFeatureFlag } from "services/Flags";
import {
  getMembershipRewardGroupSubheader,
  getProductTitle,
  getProductTitleQuantity,
  hasBeautyBankAvailable,
} from "services/HelperService";
import { formatCurrency } from "toolbox/Money";
import { getBillingPeriodIntervalTreatmentSelection } from "utils/billingPeriod.helpers";

type MembershipRewardsProps = {
  membershipRewards: Models<"MembershipRewardModel">[];
  rewardGroup?: Models<"MembershipRewardGroupModel">;
  description?: string;
  inactiveMembership?: boolean | null;
  svg: FC<SVGProps<SVGSVGElement>>;
  billingPeriodsBeforeFirstReward?: number | null;
  billingPeriodInterval?: number;
  membershipBillingPeriodInterval?: string;
  membershipBillingPeriodIntervalCount?: number;
  isSelectable?: boolean;
  quantityPerPeriod?: number;
  flagBeautyBank?: boolean;
  hasAnyImagesOnPage?: boolean;
};

export const MembershipRewards = ({
  membershipRewards,
  rewardGroup,
  description,
  inactiveMembership,
  svg,
  billingPeriodsBeforeFirstReward,
  billingPeriodInterval = 1,
  membershipBillingPeriodInterval = "MONTH",
  membershipBillingPeriodIntervalCount = 1,
  isSelectable = false,
  quantityPerPeriod,
  flagBeautyBank = false,
  hasAnyImagesOnPage = false,
}: MembershipRewardsProps) => {
  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );
  const isV3Enabled = true;
  const beautyBankTerm = useFeatureFlag("beautyBankTerm");

  if (!membershipRewards || membershipRewards.length === 0) {
    return null;
  }

  const periodCount =
    membershipBillingPeriodIntervalCount * billingPeriodInterval;

  const treatmentSelection = getBillingPeriodIntervalTreatmentSelection(
    membershipBillingPeriodIntervalCount,
    membershipBillingPeriodInterval,
    isFlexibleMembershipsEnabled,
    periodCount,
    billingPeriodInterval
  );

  const treatmentSelectionSubheader = getMembershipRewardGroupSubheader(
    billingPeriodsBeforeFirstReward,
    membershipBillingPeriodIntervalCount,
    membershipBillingPeriodInterval
  );

  const shouldShowStartingText =
    billingPeriodsBeforeFirstReward !== null &&
    billingPeriodsBeforeFirstReward !== undefined &&
    billingPeriodsBeforeFirstReward > 1;

  const hasBeautyBankCredit =
    rewardGroup && hasBeautyBankAvailable(rewardGroup);

  const membershipRewardsForExpandable = membershipRewards.map((mr) => ({
    id: mr.id,
    title:
      getProductTitle(mr.packageListingTitle, mr.packageName) +
      " " +
      getProductTitleQuantity(mr.quantity, mr.packageUnitType),
    description: mr.packageDescription,
  }));

  const expandableItems =
    flagBeautyBank && hasBeautyBankCredit && rewardGroup
      ? [
          ...membershipRewardsForExpandable,
          {
            id: "beautyBank",
            title: `${beautyBankTerm} RepeatCash`,
            description: `Bank ${formatCurrency(
              rewardGroup.beautyBank?.creditAmount ?? 0,
              0
            )} for use on future services - use anytime!`,
          },
        ]
      : membershipRewardsForExpandable;

  // Determine the title based on isSelectable
  const headerTitle = isSelectable
    ? `Choose ${
        quantityPerPeriod ?? 1
      } Of The Following Every ${treatmentSelection}`
    : `Enjoy All Of The Following Every ${treatmentSelection}`;

  const headerTitleV2 = isSelectable
    ? `CHOOSE ${
        quantityPerPeriod ?? 1
      } OF THE FOLLOWING EVERY ${treatmentSelection}`
    : `ENJOY ALL OF THE FOLLOWING EVERY ${treatmentSelection}`;

  if (isV3Enabled) {
    return (
      <div className="bg-white">
        <div className="px-4 py-6">
          <div className="mb-3 flex flex-row items-center gap-2">
            <Icon
              color={inactiveMembership ? "darkGrey" : "brandColor"}
              size="x2small"
              svg={svg}
              className="flex-shrink-0"
            />
            <span className="text-h4 font-serif font-semibold text-primary">
              {headerTitle}
            </span>
          </div>
          <IfFlag flag="timeDelayedTreatmentGroups">
            {shouldShowStartingText && (
              <div className="pb-1 text-body2 text-secondary">
                Starting on your{" "}
                <span className="text-bold2">
                  {treatmentSelectionSubheader}
                </span>{" "}
                as a member
              </div>
            )}
          </IfFlag>
          {description && (
            <div
              className={cx(
                "pb-2 text-body2",
                inactiveMembership ? "text-dark-grey" : "text-secondary"
              )}
            >
              {description}
            </div>
          )}
          <div className="divide-border-one divide-y">
            {membershipRewards.map((mr) => {
              const title = getProductTitle(
                mr.packageListingTitle,
                mr.packageName
              );
              const quantityText = getProductTitleQuantity(
                mr.quantity,
                mr.packageUnitType
              );
              return (
                <div key={mr.id} className="px-3 py-2.5">
                  <div className="text-left text-body2 text-primary">
                    {quantityText && mr.quantity > 1 && (
                      <span className="font-semibold">{quantityText} </span>
                    )}
                    <span className="font-semibold">{title}</span>
                    {mr.packageDescription && (
                      <p className="mt-1 line-clamp-4 font-normal text-secondary">
                        {mr.packageDescription}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {flagBeautyBank && hasBeautyBankCredit && rewardGroup && (
            <div className="relative mt-3 flex h-24 w-full items-center overflow-hidden rounded-lg bg-gradient-to-br from-gray-900 via-black to-gray-800 shadow-subtle">
              {/* Animated background */}
              <AnimatedBackground
                mode="black"
                durationSeconds={10}
                className="absolute inset-0 h-full w-full opacity-50"
              />

              {/* Subtle card pattern */}
              <div
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                  backgroundSize: "32px 32px",
                }}
              />

              {/* Plus signs */}
              <div className="absolute right-4 top-2 text-xl font-light text-white opacity-15">
                +
              </div>
              <div className="absolute bottom-2 left-12 text-2xl font-light text-white opacity-20">
                +
              </div>

              <div className="relative flex h-24 w-24 flex-shrink-0 items-center justify-center">
                <Icon svg={RepeatCash} size="xsmall" color="white" />
              </div>
              <div className="relative flex flex-1 flex-col overflow-hidden py-2 pl-2 pr-2">
                <div className="text-body3 font-semibold text-white/60">
                  {formatCurrency(rewardGroup.beautyBank?.creditAmount ?? 0, 0)}
                </div>
                <div className="truncate text-bold2 text-white">
                  {beautyBankTerm} RepeatCash
                </div>
                <div className="line-clamp-2 text-body3 text-white/80">
                  Bank for use on future services - use anytime!
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // V2 design
  return (
    <div className={cx("mb-1 bg-white py-8", isSelectable ? "px-4" : "px-6")}>
      <div className={cx("mb-6 text-center", isSelectable && "mb-4")}>
        <div className="flex w-full justify-center">
          <Icon
            color={inactiveMembership ? "darkGrey" : "brandColor"}
            size="xsmall"
            svg={svg}
          />
        </div>
        <div
          className={cx(
            "py-4 text-sub1 uppercase",
            inactiveMembership ? "text-dark-grey" : "text-brand-color"
          )}
        >
          {headerTitleV2.split(" EVERY ")[0]}
          <br />
          EVERY {headerTitleV2.split(" EVERY ")[1]}
        </div>
        <IfFlag flag="timeDelayedTreatmentGroups">
          <div className="text-secondary">
            Starting on your{" "}
            <span className="text-bold1">{treatmentSelectionSubheader}</span> as
            a member
          </div>
        </IfFlag>
        {description && (
          <div
            className={cx(
              "text-body1",
              inactiveMembership ? "text-dark-grey" : "text-secondary"
            )}
          >
            {description}
          </div>
        )}
      </div>

      {isSelectable ? (
        <div className="flex flex-col items-center rounded-lg border-solid border-light-grey">
          <div className="mb-4 w-full px-2">
            <ExpandableItems items={expandableItems} />
          </div>
        </div>
      ) : (
        <ExpandableItems
          items={expandableItems}
          disabled={inactiveMembership || null}
        />
      )}
    </div>
  );
};
